import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, TrendingUp, Activity, Sparkles, Copy,
  Compass, BrainCircuit, Stethoscope,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMPEL_STYLE = {
  gruen:  { dot: "bg-sage-dark",       label: "geringe Einschränkungen",        text: "text-sage-dark"   },
  gelb:   { dot: "bg-saffron",         label: "mittlere Einschränkungen",       text: "text-clay-dark"   },
  rot:    { dot: "bg-brand-darkred",   label: "hohe Einschränkungen",           text: "text-brand-darkred" },
};

const AMPEL_EMOJI = { gruen: "🟢", gelb: "🟡", rot: "🔴" };

const ANS_BTN = (active) =>
  `flex-1 min-w-0 h-9 rounded-lg border text-[11.5px] font-semibold transition-colors px-2 leading-tight ${
    active
      ? "bg-sand-800 text-cream-50 border-sand-800"
      : "bg-white border-cream-300 text-sand-700 hover:border-terra"
  }`;


/** Fragebogen-Sub-Tab: NBA-Bewertungslogik mit Pflegegrad-Radar + Höherstufungs-Booster. */
export default function PkiNbaFragebogen({ customerLabel }) {
  const [schema, setSchema] = useState(null);
  const [view, setView] = useState("fragebogen");  // fragebogen | booster
  const [antworten, setAntworten] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Booster
  const [vorher, setVorher] = useState({});
  const [jetzt, setJetzt] = useState({});
  const [aktPg, setAktPg] = useState("");
  const [zielPg, setZielPg] = useState("");
  const [boosterResult, setBoosterResult] = useState(null);
  const [boosterBusy, setBoosterBusy] = useState(false);

  // Schema laden
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pflege-ki/nba-score/questions`, { withCredentials: true });
        setSchema(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Fragebogen konnte nicht geladen werden.");
      }
    })();
  }, []);

  const total = useMemo(() => {
    if (!schema) return 0;
    return schema.module.reduce((s, m) => s + m.fragen.length, 0);
  }, [schema]);

  const beantwortet = useMemo(() => {
    return Object.values(antworten).filter(Boolean).length;
  }, [antworten]);

  const set = (fid, key) => setAntworten((p) => ({ ...p, [fid]: key }));

  const runScore = async () => {
    if (beantwortet < 5) {
      toast.error("Bitte mindestens 5 Fragen beantworten.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/nba-score/assessment`, {
        antworten,
        customer_label: customerLabel || null,
      }, { withCredentials: true });
      setResult(r.data);
      toast.success(`Auswertung fertig · PG-Tendenz ${r.data.pg_tendenz?.label}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auswertung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setAntworten({});
    setResult(null);
    toast.message("Eingaben zurückgesetzt.");
  };

  const runBooster = async () => {
    const vCount = Object.values(vorher).filter(Boolean).length;
    const jCount = Object.values(jetzt).filter(Boolean).length;
    if (vCount < 5 || jCount < 5) {
      toast.error("Bitte mindestens 5 Fragen pro Spalte (Vorher / Jetzt) beantworten.");
      return;
    }
    setBoosterBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/nba-score/upgrade-check`, {
        vorher_antworten: vorher,
        jetzt_antworten: jetzt,
        aktueller_pflegegrad: aktPg ? Number(aktPg) : null,
        gewuenschter_pflegegrad: zielPg ? Number(zielPg) : null,
        customer_label: customerLabel || null,
      }, { withCredentials: true });
      setBoosterResult(r.data);
      toast.success("Höherstufungs-Analyse fertig.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booster fehlgeschlagen.");
    } finally {
      setBoosterBusy(false);
    }
  };

  const copyText = async (txt) => {
    try { await navigator.clipboard.writeText(txt || ""); toast.success("Kopiert."); }
    catch { toast.error("Kopieren fehlgeschlagen."); }
  };

  // Antwort-Skala aus Schema
  const scale = schema?.antwort_skala || [];

  if (!schema) {
    return <div className="text-sand-600 text-sm p-6">Lade Fragebogen …</div>;
  }

  return (
    <div className="space-y-5" data-testid="nba-fragebogen-tab">
      {/* SUB-VIEW TOGGLE */}
      <div className="rounded-2xl bg-cream-100 p-1.5 inline-flex gap-1 flex-wrap" data-testid="nba-subview-toggle">
        <button onClick={() => setView("fragebogen")}
          data-testid="nba-sub-fragebogen"
          className={`px-3.5 h-9 rounded-xl text-[12.5px] font-semibold transition-colors ${
            view === "fragebogen" ? "bg-sand-800 text-cream-50" : "text-sand-700 hover:text-sand-800"
          }`}>
          Pflegegrad-Radar
        </button>
        <button onClick={() => setView("booster")}
          data-testid="nba-sub-booster"
          className={`px-3.5 h-9 rounded-xl text-[12.5px] font-semibold transition-colors ${
            view === "booster" ? "bg-sand-800 text-cream-50" : "text-sand-700 hover:text-sand-800"
          }`}>
          Höherstufungs-Booster
        </button>
        <button onClick={() => setView("tools")}
          data-testid="nba-sub-tools"
          className={`px-3.5 h-9 rounded-xl text-[12.5px] font-semibold transition-colors ${
            view === "tools" ? "bg-sand-800 text-cream-50" : "text-sand-700 hover:text-sand-800"
          }`}>
          Diagnose &amp; Demenz-Tools
        </button>
      </div>

      {/* ──────────────────── FRAGEBOGEN ──────────────────── */}
      {view === "fragebogen" && (
        <>
          {/* Progress + Action-Bar */}
          <div className="rounded-2xl border border-cream-300 bg-white p-5 flex items-center justify-between flex-wrap gap-3"
            data-testid="nba-fb-actionbar">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                Fragebogen · NBA-Module
              </div>
              <div className="text-[13.5px] text-sand-700 mt-1">
                Beantwortet: <span className="font-bold text-sand-800">{beantwortet}</span> / {total} Fragen
              </div>
              <div className="mt-2 h-1.5 w-44 rounded-full bg-cream-200 overflow-hidden">
                <div className="h-full bg-sage-dark transition-all"
                  style={{ width: `${total ? (beantwortet / total) * 100 : 0}%` }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={resetForm} variant="outline" size="sm"
                data-testid="nba-fb-reset"
                className="h-10 rounded-full border-clay">
                Zurücksetzen
              </Button>
              <Button onClick={runScore} disabled={busy}
                data-testid="nba-fb-run"
                className="h-10 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white px-5">
                <Activity className="h-4 w-4 mr-1.5" strokeWidth={2} />
                {busy ? "Berechne …" : "Pflegegrad-Tendenz berechnen"}
              </Button>
            </div>
          </div>

          {/* Module mit Fragen */}
          {schema.module.map((m) => (
            <div key={m.modul_id} className="rounded-2xl border border-cream-300 bg-white p-5"
              data-testid={`nba-fb-modul-${m.modul_id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                    Modul {m.modul_nummer} · {m.modul_name}
                  </div>
                  <div className="text-[11.5px] text-sand-500 mt-0.5">
                    Gewicht im Gesamtscore: {m.gewicht_prozent}%
                    {m.max_with && <span className="ml-1">· nur größerer Wert von Modul 2/3 zählt</span>}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {m.fragen.map((f) => (
                  <div key={f.id} className="space-y-1.5" data-testid={`nba-fb-frage-${f.id}`}>
                    <div className="text-[13px] text-sand-800 font-medium">{f.frage}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {scale.map((a) => (
                        <button key={a.key}
                          onClick={() => set(f.id, a.key)}
                          data-testid={`nba-fb-ans-${f.id}-${a.key}`}
                          className={ANS_BTN(antworten[f.id] === a.key)}
                          type="button">
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* RADAR-ERGEBNIS */}
          {result && <RadarErgebnis result={result} />}
        </>
      )}

      {/* ──────────────────── HÖHERSTUFUNGS-BOOSTER ──────────────────── */}
      {view === "booster" && (
        <>
          <div className="rounded-2xl border border-cream-300 bg-cream-50 p-5"
            data-testid="nba-boost-info">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-clay-dark" strokeWidth={2} />
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                Höherstufungs-Booster
              </div>
            </div>
            <h3 className="font-display text-xl text-sand-800 mt-1">
              Vorher / Jetzt vergleichen — fachliche Begründung erzeugen
            </h3>
            <p className="text-[13px] text-sand-600 mt-1 leading-snug max-w-3xl">
              Beantworte für beide Spalten dieselben Fragen (vor der letzten Begutachtung
              und aktuell). Die KI erkennt automatisch Verschlechterungen je NBA-Modul
              und schlägt eine pflegefachliche Begründung für die Höherstufung vor.
            </p>
          </div>

          {/* PG-Angabe */}
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                Aktueller PG
              </label>
              <input type="number" min={0} max={5} value={aktPg}
                onChange={(e) => setAktPg(e.target.value)}
                data-testid="nba-boost-akt-pg"
                className="w-full h-10 rounded-xl border border-cream-300 bg-white px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                Gewünschter PG
              </label>
              <input type="number" min={0} max={5} value={zielPg}
                onChange={(e) => setZielPg(e.target.value)}
                data-testid="nba-boost-ziel-pg"
                className="w-full h-10 rounded-xl border border-cream-300 bg-white px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra" />
            </div>
            <div className="col-span-2 flex items-end justify-end">
              <Button onClick={runBooster} disabled={boosterBusy}
                data-testid="nba-boost-run"
                className="h-10 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white px-5">
                <Sparkles className="h-4 w-4 mr-1.5" strokeWidth={2} />
                {boosterBusy ? "Erstelle Begründung …" : "Booster starten"}
              </Button>
            </div>
          </div>

          {/* 2-Spalten-Fragebogen */}
          {schema.module.map((m) => (
            <div key={m.modul_id} className="rounded-2xl border border-cream-300 bg-white p-5"
              data-testid={`nba-boost-modul-${m.modul_id}`}>
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
                Modul {m.modul_nummer} · {m.modul_name}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {/* VORHER */}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-sand-500 mb-2">
                    Vorher (letzte Begutachtung)
                  </div>
                  <BoosterColumn fragen={m.fragen} scale={scale} answers={vorher}
                    setAnswer={(fid, key) => setVorher((p) => ({ ...p, [fid]: key }))}
                    side="vorher" />
                </div>
                {/* JETZT */}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-darkred mb-2">
                    Jetzt (aktueller Zustand)
                  </div>
                  <BoosterColumn fragen={m.fragen} scale={scale} answers={jetzt}
                    setAnswer={(fid, key) => setJetzt((p) => ({ ...p, [fid]: key }))}
                    side="jetzt" />
                </div>
              </div>
            </div>
          ))}

          {/* BOOSTER-ERGEBNIS */}
          {boosterResult && (
            <BoosterErgebnis result={boosterResult} onCopy={copyText} />
          )}
        </>
      )}

      {/* ──────────────────── DIAGNOSE & DEMENZ TOOLS ──────────────────── */}
      {view === "tools" && <NbaToolsPanel onCopy={copyText} />}
    </div>
  );
}


/* ─────────── Booster · Mini-Frage-Spalte ─────────── */
function BoosterColumn({ fragen, scale, answers, setAnswer, side }) {
  return (
    <div className="space-y-2.5">
      {fragen.map((f) => (
        <div key={f.id}>
          <div className="text-[12px] text-sand-700 mb-1 leading-snug">{f.frage}</div>
          <div className="flex flex-wrap gap-1">
            {scale.map((a) => (
              <button key={a.key}
                onClick={() => setAnswer(f.id, a.key)}
                data-testid={`nba-boost-${side}-${f.id}-${a.key}`}
                type="button"
                className={`h-7 rounded-md border text-[11px] font-semibold px-2 transition-colors ${
                  answers[f.id] === a.key
                    ? "bg-sand-800 text-cream-50 border-sand-800"
                    : "bg-white border-cream-300 text-sand-600 hover:border-terra"
                }`}>
                {a.label.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


/* ─────────── Radar-Ergebnis-Karte ─────────── */
function RadarErgebnis({ result }) {
  const amp = AMPEL_STYLE[result.ampel] || AMPEL_STYLE.gelb;
  return (
    <div className="space-y-4" data-testid="nba-fb-result">

      {/* HERO-RADAR */}
      <div className="rounded-3xl border border-cream-300 bg-gradient-to-br from-cream-50 to-white p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              Pflegegrad-Radar
            </div>
            <div className="mt-2 flex items-center gap-3">
              <span className={`inline-flex h-3 w-3 rounded-full ${amp.dot}`} />
              <span className={`text-[14px] font-semibold ${amp.text}`}>{amp.label}</span>
            </div>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-display text-5xl text-sand-800 leading-none">
                {result.gesamt_score?.toFixed?.(1)}
              </span>
              <span className="text-[13px] text-sand-500">/ 100 Gesamtscore</span>
            </div>
            <div className="mt-2 text-[14px] font-semibold text-sand-800">
              Tendenz: <span className="text-brand-darkred">{result.pg_tendenz_range?.label || result.pg_tendenz?.label}</span>
            </div>
            {result.cockpit?.max_logik_hinweis && (
              <div className="mt-2 text-[11.5px] text-sand-500 italic max-w-md">
                {result.cockpit.max_logik_hinweis}
              </div>
            )}
          </div>
          <div className="text-[11.5px] text-sand-500 italic max-w-sm">
            {result.disclaimer}
          </div>
        </div>
      </div>

      {/* NBA-COCKPIT · Ampeln je Modul + Top-Ranking */}
      {result.cockpit?.rows && (
        <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="nba-cockpit">
          <div className="flex items-center gap-2 mb-3">
            <Compass className="h-4 w-4 text-clay-dark" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              NBA-Cockpit · Ampel-Übersicht
            </div>
          </div>
          <ul className="space-y-1.5">
            {result.cockpit.rows.map((r) => (
              <li key={r.modul_id}
                data-testid={`nba-cockpit-row-${r.modul_id}`}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-[12.5px] ${
                  r.zaehlt_in_score ? "bg-cream-50" : "bg-cream-100 opacity-70"
                }`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-[16px] leading-none">{AMPEL_EMOJI[r.modul_ampel]}</span>
                  <span className="font-semibold text-sand-800 truncate">
                    Modul {r.modul_nummer} · {r.modul_name}
                  </span>
                  {!r.zaehlt_in_score && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-sand-500">
                      (zählt nicht — max-Logik)
                    </span>
                  )}
                </div>
                <div className="text-sand-600 tabular-nums shrink-0 ml-2">
                  {r.score_0_100}% · Gew. {r.gewicht_prozent}%
                </div>
              </li>
            ))}
          </ul>

          {/* Top-Einschränkungen */}
          {result.cockpit.top_einschraenkungen?.length > 0 && (
            <div className="mt-4 rounded-xl border border-brand-darkred/30 bg-brand-darkred/5 p-3"
              data-testid="nba-cockpit-top">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-brand-darkred mb-1">
                Stärkste Einschränkungen
              </div>
              <ol className="list-decimal list-inside text-[12.5px] text-sand-800 space-y-0.5">
                {result.cockpit.top_einschraenkungen.map((t, i) => (
                  <li key={t.modul_id}>
                    <strong>{t.modul_name}</strong>{" "}
                    <span className="text-sand-500">· Beitrag {t.beitrag_zaehlt} Punkte</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* MODUL-SCORES */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
          Modul-Auswertung
        </div>
        <ul className="space-y-2.5">
          {result.modul_scores.map((m) => {
            const pct = m.score_0_100;
            return (
              <li key={m.modul_id} data-testid={`nba-result-modul-${m.modul_id}`}>
                <div className="flex items-center justify-between text-[12.5px] mb-1">
                  <span className="font-semibold text-sand-800">
                    Modul {m.modul_nummer} · {m.modul_name}
                  </span>
                  <span className="text-sand-600 tabular-nums">
                    {pct}% · Gewicht {m.gewicht_prozent}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-cream-200 overflow-hidden">
                  <div
                    className={`h-full ${
                      pct >= 70 ? "bg-brand-darkred" : pct >= 35 ? "bg-saffron" : "bg-sage-dark"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-[11px] text-sand-500 mt-0.5">
                  {m.beantwortet} / {m.fragen_gesamt} beantwortet
                  {m.schwere_befunde?.length > 0 && (
                    <span className="ml-2 text-brand-darkred">
                      · {m.schwere_befunde.length} schwere Befunde
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* FEHLENDE ANGABEN */}
      {result.fehlende_fragen_zusammenfassung?.length > 0 && (
        <div className="rounded-2xl border border-terra/30 bg-peach-50 p-5"
          data-testid="nba-result-missing">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-terra" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra">
              Für eine belastbare Einschätzung fehlen noch Antworten
            </div>
          </div>
          <ul className="space-y-2 text-[12.5px] text-sand-700">
            {result.fehlende_fragen_zusammenfassung.map((m) => (
              <li key={m.modul_id}>
                <strong>{m.modul_name}:</strong>{" "}
                {m.fehlt.slice(0, 3).join(" · ")}
                {m.fehlt.length > 3 && ` … +${m.fehlt.length - 3} weitere`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* SCHWERE BEFUNDE */}
      {result.schwere_befunde_alle?.length > 0 && (
        <div className="rounded-2xl border border-cream-300 bg-white p-5"
          data-testid="nba-result-schwer">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-4 w-4 text-brand-darkred" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              Schwere Befunde ({result.schwere_befunde_alle.length})
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-1.5 text-[12.5px] text-sand-700">
            {result.schwere_befunde_alle.map((s, i) => (
              <li key={i} className="rounded-md bg-brand-darkred/5 border border-brand-darkred/20 px-2.5 py-1.5">
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-darkred">
                  {s.modul}
                </span>
                <div className="text-sand-800 leading-snug">{s.befund}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


/* ─────────── Booster-Ergebnis-Karte ─────────── */
function BoosterErgebnis({ result, onCopy }) {
  const diff = result.diff || {};
  const isBetter = diff.gesamt_delta < 0;
  return (
    <div className="space-y-4" data-testid="nba-boost-result">
      {/* DELTA-KARTE */}
      <div className="rounded-3xl border border-cream-300 bg-gradient-to-br from-cream-50 to-white p-6">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
          Vorher → Jetzt · Übersicht
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-3">
          <div>
            <div className="text-[11px] text-sand-500 uppercase tracking-[0.22em]">Score-Delta</div>
            <div className={`font-display text-4xl mt-1 ${
              isBetter ? "text-sage-dark" : diff.gesamt_delta > 0 ? "text-brand-darkred" : "text-sand-800"
            }`}>
              {diff.gesamt_delta > 0 ? "+" : ""}{diff.gesamt_delta}
            </div>
            <div className="text-[11.5px] text-sand-500 mt-1">Punkte</div>
          </div>
          <div>
            <div className="text-[11px] text-sand-500 uppercase tracking-[0.22em]">Pflegegrad-Tendenz</div>
            <div className="font-display text-3xl mt-1 text-sand-800">
              PG{diff.pg_vorher} <span className="text-clay-dark mx-1">→</span> PG{diff.pg_jetzt}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-sand-500 uppercase tracking-[0.22em]">Ampel</div>
            <div className="font-display text-3xl mt-1 text-sand-800">
              {(AMPEL_STYLE[diff.ampel_vorher]?.label || "—").split(" ")[0]}
              <span className="text-clay-dark mx-1">→</span>
              {(AMPEL_STYLE[diff.ampel_jetzt]?.label || "—").split(" ")[0]}
            </div>
          </div>
        </div>
      </div>

      {/* MODUL-DELTAS */}
      {diff.modul_deltas?.length > 0 && (
        <div className="rounded-2xl border border-cream-300 bg-white p-5"
          data-testid="nba-boost-deltas">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
            Veränderung je NBA-Modul
          </div>
          <ul className="space-y-2">
            {diff.modul_deltas.map((d) => (
              <li key={d.modul_id}
                className={`rounded-xl border p-3 text-[12.5px] ${
                  d.delta > 0
                    ? "bg-brand-darkred/5 border-brand-darkred/30"
                    : "bg-sage-dark/5 border-sage-dark/30"
                }`}>
                <div className="font-semibold text-sand-800">{d.modul_name}</div>
                <div className="text-sand-700">
                  Score: {d.vorher_score} → {d.jetzt_score}{" "}
                  <span className={`font-bold ${d.delta > 0 ? "text-brand-darkred" : "text-sage-dark"}`}>
                    ({d.delta > 0 ? "+" : ""}{d.delta})
                  </span>{" "}
                  · {d.richtung}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* BEGRÜNDUNG */}
      {result.begruendung && (
        <div className="rounded-2xl border border-cream-300 bg-white p-5"
          data-testid="nba-boost-begruendung">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-brand-darkred" strokeWidth={2} />
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                Pflegefachliche Begründung · Entwurf
              </div>
            </div>
            <Button onClick={() => onCopy(result.begruendung)} variant="outline" size="sm"
              data-testid="nba-boost-copy"
              className="h-9 rounded-full border-clay">
              <Copy className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
              Text kopieren
            </Button>
          </div>
          <div className="rounded-xl border border-cream-200 bg-cream-50 px-4 py-3 text-[14px] leading-relaxed text-sand-800 font-mono whitespace-pre-wrap"
            data-testid="nba-boost-begruendung-text">
            {result.begruendung}
          </div>
          <div className="mt-3 text-[11.5px] text-brand-darkred font-semibold">
            {result.disclaimer}
          </div>
        </div>
      )}
    </div>
  );
}



/* ─────────── NBA-Tools · Diagnose-Übersetzer + Demenz-Engine ─────────── */
function NbaToolsPanel({ onCopy }) {
  // Diagnose-Übersetzer
  const [diagText, setDiagText] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  // Demenz-Engine
  const [demText, setDemText] = useState("");
  const [demResult, setDemResult] = useState(null);
  const [demBusy, setDemBusy] = useState(false);

  const runDiag = async () => {
    if (!diagText.trim()) { toast.error("Bitte Diagnosen eingeben."); return; }
    setDiagBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/nba-score/diagnose-translate`,
        { text: diagText }, { withCredentials: true });
      setDiagResult(r.data);
      toast.success(`${r.data.anzahl} Diagnose(n) erkannt.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Übersetzung fehlgeschlagen.");
    } finally { setDiagBusy(false); }
  };

  const runDem = async () => {
    if (!demText.trim()) { toast.error("Bitte SIS-Auszug eingeben."); return; }
    setDemBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/nba-score/demenz-scan`,
        { text: demText }, { withCredentials: true });
      setDemResult(r.data);
      toast.success(`${r.data.matches.length} Treffer in Modul 2/3.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demenz-Scan fehlgeschlagen.");
    } finally { setDemBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="nba-tools-panel">

      {/* DIAGNOSE-ÜBERSETZER */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5"
        data-testid="nba-diagnose-card">
        <div className="flex items-center gap-2 mb-1">
          <Stethoscope className="h-4 w-4 text-clay-dark" strokeWidth={2} />
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
            Hilfebedarfs-Übersetzer
          </div>
        </div>
        <h3 className="font-display text-xl text-sand-800">
          Diagnose → konkreter Hilfebedarf
        </h3>
        <p className="text-[12.5px] text-sand-600 mt-1 leading-snug max-w-3xl">
          MD-Gutachter wollen Hilfebedarf, keine Diagnosen. Diese Engine zeigt dir,
          welche NBA-Auswirkungen typischerweise mit der jeweiligen Diagnose verbunden
          sind — als deine Checkliste vor der Begutachtung.
        </p>

        <textarea
          rows={3}
          value={diagText}
          onChange={(e) => setDiagText(e.target.value)}
          placeholder="z. B. Parkinson, Arthrose, Herzinsuffizienz, Diabetes mellitus …"
          data-testid="nba-diag-input"
          className="mt-3 w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra"
        />
        <div className="mt-3 flex justify-end">
          <Button onClick={runDiag} disabled={diagBusy}
            data-testid="nba-diag-run"
            className="h-10 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white px-5">
            {diagBusy ? "Analysiere …" : "Übersetzen"}
          </Button>
        </div>

        {diagResult && (
          <div className="mt-4 space-y-3" data-testid="nba-diag-result">
            <div className="text-[12px] text-sand-600">
              <strong>{diagResult.anzahl}</strong> Diagnose(n) erkannt ·
              Betroffene Module: {diagResult.module_betroffen.length}
            </div>
            {diagResult.erkannte_diagnosen.map((d, i) => (
              <div key={i} className="rounded-xl border border-cream-300 bg-cream-50 p-3"
                data-testid={`nba-diag-card-${i}`}>
                <div className="text-[13.5px] font-bold text-sand-800">{d.label}</div>
                <div className="text-[11.5px] text-sand-500 mt-0.5">
                  Module: {d.module.join(" · ")}
                </div>
                <ul className="mt-2 list-disc list-inside text-[12.5px] text-sand-700 space-y-0.5">
                  {d.auswirkungen.map((a, j) => <li key={j}>{a}</li>)}
                </ul>
                <div className="mt-2 text-[12px] text-clay-dark italic leading-snug">
                  💡 MD-Tipp: {d.md_tipp}
                </div>
                <button
                  onClick={() => onCopy(`${d.label}\n\nMögliche Auswirkungen:\n` +
                    d.auswirkungen.map((a) => `- ${a}`).join("\n") +
                    `\n\nMD-Tipp: ${d.md_tipp}`)}
                  data-testid={`nba-diag-copy-${i}`}
                  className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-brand-darkred hover:underline">
                  <Copy className="h-3 w-3" strokeWidth={2} /> Diese Liste kopieren
                </button>
              </div>
            ))}
            <div className="text-[11px] text-sand-500 italic">{diagResult.disclaimer}</div>
          </div>
        )}
      </div>

      {/* DEMENZ-ENGINE */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5"
        data-testid="nba-demenz-card">
        <div className="flex items-center gap-2 mb-1">
          <BrainCircuit className="h-4 w-4 text-clay-dark" strokeWidth={2} />
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
            Demenz-Engine · Modul 2 + 3 Trigger
          </div>
        </div>
        <h3 className="font-display text-xl text-sand-800">
          SIS-Auszug → erwartete NBA-Module 2 &amp; 3
        </h3>
        <p className="text-[12.5px] text-sand-600 mt-1 leading-snug max-w-3xl">
          Modul 2 (Kognition) und Modul 3 (Verhalten) entscheiden oft über Höherstufungen.
          Diese Engine erkennt typische Stichwörter und zeigt dir, welche konkreten
          NBA-Fragen bei der Begutachtung beantwortet werden sollten.
        </p>

        <textarea
          rows={4}
          value={demText}
          onChange={(e) => setDemText(e.target.value)}
          placeholder='z. B. "Vergisst Termine, erkennt Angehörige teilweise nicht, stellt dieselbe Frage mehrfach, nachts unruhig." '
          data-testid="nba-dem-input"
          className="mt-3 w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra"
        />
        <div className="mt-3 flex justify-end">
          <Button onClick={runDem} disabled={demBusy}
            data-testid="nba-dem-run"
            className="h-10 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white px-5">
            {demBusy ? "Analysiere …" : "Demenz-Scan starten"}
          </Button>
        </div>

        {demResult && (
          <div className="mt-4 space-y-3" data-testid="nba-dem-result">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-cream-300 bg-cream-50 p-3"
                data-testid="nba-dem-modul2">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-clay-dark">
                  Modul 2 · Kognition ({demResult.modul_2_kognition_treffer.length})
                </div>
                {demResult.modul_2_kognition_treffer.length === 0 ? (
                  <div className="text-[12px] text-sand-500 mt-2">(keine Treffer)</div>
                ) : (
                  <ul className="mt-2 space-y-1 text-[12.5px] text-sand-800">
                    {demResult.modul_2_kognition_treffer.map((m, i) => (
                      <li key={i}>
                        <strong>{m.kategorie}</strong>
                        <div className="text-[11.5px] text-sand-500">{m.frage_zu_klaeren}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-xl border border-cream-300 bg-cream-50 p-3"
                data-testid="nba-dem-modul3">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-clay-dark">
                  Modul 3 · Verhalten ({demResult.modul_3_verhalten_treffer.length})
                </div>
                {demResult.modul_3_verhalten_treffer.length === 0 ? (
                  <div className="text-[12px] text-sand-500 mt-2">(keine Treffer)</div>
                ) : (
                  <ul className="mt-2 space-y-1 text-[12.5px] text-sand-800">
                    {demResult.modul_3_verhalten_treffer.map((m, i) => (
                      <li key={i}>
                        <strong>{m.kategorie}</strong>
                        <div className="text-[11.5px] text-sand-500">{m.frage_zu_klaeren}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {demResult.fragen_zu_klaeren.length > 0 && (
              <div className="rounded-xl border border-terra/30 bg-peach-50 p-3"
                data-testid="nba-dem-fragen">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-terra">
                  Bei der nächsten Begutachtung konkret beantworten:
                </div>
                <ul className="mt-2 list-disc list-inside text-[12.5px] text-sand-800 space-y-0.5">
                  {demResult.fragen_zu_klaeren.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            <div className="text-[11px] text-sand-500 italic">{demResult.disclaimer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
